import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, type LibrarySource } from "../api/client";
import AttributionBadge from "../components/Attribution";

interface Chunk {
  id: number;
  ordinal: number;
  text: string;
  char_start: number;
  char_end: number;
  token_count: number;
}

export default function DocumentViewer() {
  const { sourceId } = useParams();
  const navigate = useNavigate();
  const id = Number(sourceId);
  const [source, setSource] = useState<LibrarySource | null>(null);
  const [chunks, setChunks] = useState<Chunk[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    Promise.all([api.getLibrarySource(id), api.getLibraryChunks(id)])
      .then(([nextSource, nextChunks]) => {
        setSource(nextSource);
        setChunks(nextChunks);
      })
      .catch((e) => setError(String(e)));
  }, [id]);

  if (error) {
    return (
      <>
        <div className="ar-header"><h2>Document</h2></div>
        <div className="ar-body"><p className="error">{error}</p></div>
      </>
    );
  }

  if (!source) {
    return (
      <>
        <div className="ar-header"><h2>Document</h2></div>
        <div className="ar-body"><p className="ar-muted"><span className="ar-spinner" /> Loading...</p></div>
      </>
    );
  }

  const isPdf = source.mime === "application/pdf";

  return (
    <>
      <div className="ar-header">
        <h2>{source.title}</h2>
        <span className="spacer" />
        <button className="ar-btn ghost sm" type="button" onClick={() => navigate(-1)}>Back</button>
        <a className="ar-btn sm" href={api.libraryFileUrl(id)} target="_blank" rel="noreferrer">
          Open original
        </a>
      </div>
      <div className="ar-body">
        <div className="ar-doc-head">
          {source.authors && <span>{source.authors}</span>}
          {source.year && <span>{source.year}</span>}
          <AttributionBadge createdBy={source.created_by} compact />
          {source.url && <a href={source.url} target="_blank" rel="noreferrer">source URL</a>}
          <span>{source.chunk_count} chunks</span>
          <span>{source.source_category}</span>
          {source.abby_private ? <span>private</span> : null}
        </div>

        {isPdf ? (
          <iframe
            src={api.libraryFileUrl(id)}
            style={{
              width: "100%",
              height: "calc(100vh - 220px)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              background: "var(--bg-elev)",
            }}
            title={source.title}
          />
        ) : (
          <div style={{ maxWidth: 920 }}>
            <h3 style={{ marginTop: 0 }}>Extracted chunks</h3>
            {chunks.map((chunk) => (
              <details key={chunk.id} className="ar-chunk" open={chunk.ordinal === 0}>
                <summary>
                  Chunk #{chunk.ordinal + 1} · {chunk.token_count} tokens
                </summary>
                <div className="body-text">{chunk.text}</div>
              </details>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
